import React, { useState, useEffect } from 'react';
import { Form, Table, Spinner, Alert, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan, faCloudDownload, faSort, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import {
  isBondCounselUser,
  isOfficeOfTheAttorneyGeneralUser,
  isBondReviewUser,
  isMacUser,
  isDisabledBasedOnBusinessLogic,
  roleFilteredBondStatus,
  is90DaysAfterRegistrationComplete,
  isTwbUser,
  isTeaUser,
} from "../services/AuthorizationRules";

import {
  getPresignedAttachmentUrl,
  deleteMultipleAttachmentsOnBond,
  getBulkPresignedAttachmentUrl,
  updateMultipleAttachmentOnBond,
  getBondsDataByIdService
} from "../services/BondsServices";
import {
  BOND_ACTION,
} from "../types";
import JSZip from 'jszip';


const AttachmentTable = ({
  showLoadingSpinner,
  searchTerm,
  handleSearch,
  filteredAttachment,
  setFilteredAttachment,
  user,
  bondPageData,
  id,
  handleRequestDeleteConfirmation,
}) => {
  const [selectedAttachments, setSelectedAttachments] = useState([]);
  const [checkAll, setCheckAll] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [sortDirection, setSortDirection] = useState("ascending");
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const handleCheckboxChange = (attachmentName) => {    
    setSelectedAttachments((prevSelected) => {
      const isSelected = prevSelected.some(
        (attachment) => attachment.Attachment_Name === attachmentName.Attachment_Name
      );

      if (isSelected) {
        // If attachment is already in the list, remove it
        return prevSelected.filter(
          (attachment) => attachment.Attachment_Name !== attachmentName.Attachment_Name
        );
      } else {
        // If attachment is not in the list, add it
        return [...prevSelected, attachmentName];
      }
    });
    setCheckAll(false);
  };


  useEffect(() => {
  }, [selectedAttachments]);

  useEffect(() => {
  }, [bondPageData]);

  useEffect(() => {
    setSelectedAttachments(prev =>
      prev.filter(sel =>
        filteredAttachment.some(
          att => att.Attachment_Name === sel.Attachment_Name
        )
      )
    );
  }, [filteredAttachment]);

  // Update local input value when selectedData changes

  const handleCheckAllChange = () => {
    if (checkAll) {
      setSelectedAttachments([]);
    } else {
      setSelectedAttachments(filteredAttachment.map((attachment) => attachment));
    }
    setCheckAll(!checkAll);
  };

  // Helper function to convert a base64 string to an ArrayBuffer
  const base64ToArrayBuffer = (base64) => {
    // Remove any potential whitespace/newlines from the base64 string
    base64 = base64.replace(/\s/g, '');
    const binaryString = window.atob(base64); // decode base64 string
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  };

  const handleDownloadAllFiles = async () => {
    setShowError(false);

    if (selectedAttachments.length === 0) return;

    setIsLoading(true)
    const zip = new JSZip();

    try {
      const promises = selectedAttachments.map(
        async (attachmentName) => await getPresignedAttachmentUrl(id, { Attachment_Name: attachmentName.Attachment_Name })
      );
      const attachments = await Promise.all(promises);

      const filePromises = attachments.map(async (url, index) => {
        const file = await fetch(url, { method: 'GET', mode: 'cors', credentials: 'omit' });
        if (!file.ok) throw new Error(`Failed to fetch file at ${url}`);
        const blob = await file.blob();
        zip.file(selectedAttachments[index].Attachment_Name, blob);
        return file
      });

      const files = await Promise.all(filePromises);

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${id}_bonds_attachments.zip`; // Desired file name
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setIsLoading(false);

    }
    catch (error) {
      console.error("Error downloading or zipping files:", error);
      setShowError(true)
    }
    finally {
      setIsLoading(false);
    }
  }

  const handleApprovalToDelete = async () => {
    await handleDeleteAttachment(selectedAttachments);
    setSelectedAttachments([]);
    setShowDeleteConfirmation(false);
    setCheckAll(false);
  };

  const handleDenialToDelete = async () => {
    setSelectedAttachments([]);
    setShowDeleteConfirmation(false);
    fetchBondPageData(id);
    setCheckAll(false);
  };

  const fetchBondPageData = async (bond_id) => {
    const bondResponse = await getBondsDataByIdService(
      bond_id,
      user.userinfo.email
    );
    // setAttachmentOnBond(bondResponse?.Attachments);
    // setBondPageData(bondResponse);
    setFilteredAttachment(bondResponse.Attachments);
  };

  const handleDeleteAttachment = async function (attachmentsToDelete) {
    const attachments = attachmentsToDelete.map((attachment) => ({
      ...attachment,
      Is_Deleted_Flag: "Y",
    }));

    try {
      await Promise.all([
        deleteMultipleAttachmentsOnBond(id, attachments),
        updateMultipleAttachmentOnBond(attachments),
      ]);

      await fetchBondPageData(id);
    } catch (error) {
      console.error("Error deleting attachment:", error);
    }
  };

  const handleDownloadAll = async () => {
    console.log("selectedAttachments", selectedAttachments);

    if (selectedAttachments.length > 0) {
      const attachment_keys = selectedAttachments.map(
        (attachmentName) => `${id}/${attachmentName}`
      );

      try {
        // Get the base64-encoded ZIP from your Lambda
        const zipBase64 = await getBulkPresignedAttachmentUrl(attachment_keys);
        console.log("zipBase64", zipBase64);

        // Convert the base64 string to an ArrayBuffer
        const arrayBuffer = base64ToArrayBuffer(zipBase64);
        // Create a Blob from the ArrayBuffer with MIME type 'application/zip'
        const blob = new Blob([arrayBuffer], { type: 'application/zip' });
        // Generate an object URL for the Blob
        const url = window.URL.createObjectURL(blob);

        // Create a temporary link element to trigger the download
        const link = document.createElement('a');
        link.href = url;
        link.download = `${id}_bonds_attachments.zip`; // Desired file name
        document.body.appendChild(link);
        link.click();

        // Clean up
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error downloading zip:", error);
      }
    }
  };



  const handleSorting = (key) => {
    const sortedItems = [...filteredAttachment].sort((a, b) => {
      let itemA = a[key];
      let itemB = b[key];

      if (key === "par_dollar_amount") {
        itemA = parseFloat(itemA);
        itemB = parseFloat(itemB);
      } else {
        itemA = itemA?.toString().toUpperCase();
        itemB = itemB?.toString().toUpperCase();
      }

      if (itemA < itemB) {
        return sortDirection === "ascending" ? -1 : 1;
      }
      if (itemA > itemB) {
        return sortDirection === "ascending" ? 1 : -1;
      }
      return 0;
    });

    setFilteredAttachment(sortedItems);
    setSortDirection(
      sortDirection === "ascending" ? "descending" : "ascending"
    );
  };

  if (isLoading) {
    return (
      <div className="spinner-container">
        <Spinner role="status" aria-hidden="true">
        </Spinner>
        <span>Downloading Selected Attachments ...</span>
      </div>

    )
  }


  return (
    <div>
      {showError && <Alert variant="danger">Error downloading the files. Please try again later.</Alert>}
      {showLoadingSpinner && (
        <div>
          <button className="btn btn-primary" type="button" disabled>
            <span
              className="spinner-grow spinner-grow-sm"
              role="status"
              aria-hidden="true"
            ></span>
            Refreshing Uploaded Attachment ...
          </button>
        </div>
      )}
      {isLoading && (
        <div>
          <button className="btn btn-primary" type="button" disabled>
            <span
              className="spinner-grow spinner-grow-sm"
              role="status"
              aria-hidden="true"
            ></span>
            Downloading Selected Attachments ...
          </button>
        </div>
      )}
      {/* { <div className="tile-details-container">
        <div><span className="tile-details-label">Issuer: </span>{bondPageData.Issuer}</div>
        <div><span className="tile-details-label">Issuance Title: </span>{bondPageData.title}</div>
        <div><span className="tile-details-label">Status: </span>{bondPageData.bond_status}</div>
      </div> } */}
      <Form.Control
        type="text"
        placeholder="Search attachment by name"
        value={searchTerm}
        onChange={handleSearch}
      />
      <br />
      {selectedAttachments.length > 0 && (
        <div className="table-config">
          <button className="btn btn-primary mb-3" onClick={handleDownloadAllFiles} >
            <FontAwesomeIcon icon={faCloudDownload} /> Download Selected
          </button>
          {(isBondCounselUser(user) || isOfficeOfTheAttorneyGeneralUser(user)) &&
            (bondPageData.bond_status === "New" || bondPageData.bond_status === "Under Review") && (
            <button className="btn btn-primary mb-3" onClick={() => setShowDeleteConfirmation(true)} >
              <FontAwesomeIcon icon={faTrashAlt} /> Delete Selected
            </button> 
          )}
        </div>
      )}
      <Table>
        <thead>
          <tr style={{ whiteSpace: 'nowrap' }}>
            <th>
              <Form.Check
                type="checkbox"
                // checked={checkAll}
                checked={!(filteredAttachment.length === 0) && (selectedAttachments.length === filteredAttachment.length)}
                onChange={handleCheckAllChange}
                label="Select All"
              />
            </th>
            <th onClick={() => handleSorting("Attachment_Name")}>Attachment Name
              <span>
                <FontAwesomeIcon icon={faSort} className="ms-2" />
              </span>
            </th>

            <th onClick={() => handleSorting("CreateDttm")}>Date Time
              <span>
                <FontAwesomeIcon icon={faSort} className="ms-2" />
              </span>
            </th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {filteredAttachment &&
            filteredAttachment.map((attachment) => (
              <tr key={attachment.id || attachment.Attachment_Name}>
                <td>
                  <Form.Check
                    type="checkbox"
                    checked={selectedAttachments.some((name) => name.Attachment_Name === attachment.Attachment_Name)}
                    onChange={() => handleCheckboxChange(attachment)}
                  />
                </td>
                <td>
                  {

                    <a
                      href="#"
                      onClick={async (e) => {
                        e.preventDefault();
                        const url = await getPresignedAttachmentUrl(
                          id,
                          attachment
                        );
                        window.open(url, '_blank');
                      }}
                    >
                      {attachment.Attachment_Name}
                    </a>
                  }
                </td>
                <td>{attachment.LastUpdDttm}</td>
                <td>
                  {
                    !isBondReviewUser(user) && !isMacUser(user) && !isTwbUser(user) && !isTeaUser(user) &&
                    !isDisabledBasedOnBusinessLogic(user, bondPageData, BOND_ACTION.DELETE)
                    && (
                      <button
                        className="btn btn-link text-danger"
                        title="Delete"
                        onClick={() =>
                          handleRequestDeleteConfirmation(attachment)
                        }
                      >
                        <FontAwesomeIcon icon={faTrashCan} />
                      </button>
                    )}
                </td>
              </tr>
            ))}
        </tbody>
      </Table>
      {(isBondCounselUser(user) || isOfficeOfTheAttorneyGeneralUser(user)) && (
        <div id="footer"><p>By selecting ‘Add Attachment’ you are agreeing to not submit or upload confidential information including, but not limited to, Social Security numbers, bank account numbers or payment details.</p></div>
      )}
      <Modal show={showDeleteConfirmation}>
        <Modal.Header>
          <div>
            Are you sure you want to Delete Selected Attachments <br />
          </div>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <button
              type="button"
              class="btn btn-primary"
              onClick={() => handleDenialToDelete()}
            >
              No
            </button>
            <button
              type="button"
              class="btn btn-danger"
              onClick={() => handleApprovalToDelete()}
            >
              Yes
            </button>
          </div>
        </Modal.Body>
        <Modal.Footer></Modal.Footer>
      </Modal>
  </div>
  );
};

export default AttachmentTable;